import { useTrans } from "src/utils/useTrans";
import NextImage from "src/components/shared/NextImage";
import DownloadIcon from "@/v2-components/core/icon/download-icon";
import React from "react";
import { Button } from "@/v2-components/ui/button";
import { S3Cdn } from "@/constants/S3Cdn";

interface ManualDoc {
  title?: string;
  docLink: string;
}

interface ManualItem {
  imgLink: string;
  title: string;
  desc: string;
  version: string;
  date: string;
  docs: ManualDoc[];
}

// Pro/Air 통합 매뉴얼 (User Manual + Safety & Warranty). Manual 탭·Safety 페이지 양쪽에서 참조.
const PRO_AIR_MANUAL = `${S3Cdn.files}/tactsuit-pro-air-manual.pdf`;

// TactVisor 매뉴얼 — 일본어 판(2026-11-04 입수)은 ja 에만. 영어판과 같은 구성(7쪽 · 안전 주의 · 보증 안내 포함)의
// 번역본이라 영어판처럼 양쪽(Manual 탭 · Safety 페이지) 모두 단일 버튼.
const TACTVISOR_MANUAL = (isJa: boolean) =>
  isJa
    ? `${S3Cdn.files}/ja/tactvisor-manual.pdf`
    : `${S3Cdn.files}/tactvisor-manual-en.pdf`;

const getManualList = (locale: string): ManualItem[] => {
  const isJa = locale === "ja";
  return [
    {
      imgLink: "/images/support/downloads/download-pro.jpg",
      title: "TactSuit Pro",
      desc: "BHTV32M1D",
      version: "1.0",
      date: "2024-09-20",
      docs: isJa
        ? [
            {
              title: "User Manual",
              docLink: `${S3Cdn.files}/ja/tactsuit-pro-manual.pdf`,
            },
            {
              title: "Safety & Warranty",
              docLink: `${S3Cdn.files}/ja/tactsuit-pro-air-safety-warranty.pdf`,
            },
          ]
        : [
            {
              docLink: PRO_AIR_MANUAL,
            },
          ],
    },
    {
      imgLink: "/images/tactsuit/tactsuit-air/tactsuit-air-pair.png",
      title: "TactSuit Air",
      desc: "BHTV16M1D",
      version: "1.0",
      date: "2024-09-20",
      docs: isJa
        ? [
            {
              title: "User Manual",
              docLink: `${S3Cdn.files}/ja/tactsuit-air-manual.pdf`,
            },
            {
              title: "Safety & Warranty",
              docLink: `${S3Cdn.files}/ja/tactsuit-pro-air-safety-warranty.pdf`,
            },
          ]
        : [
            {
              docLink: PRO_AIR_MANUAL,
            },
          ],
    },
    {
      imgLink: "/images/support/downloads/download-sleeve.jpg",
      title: "TactSleeve",
      desc: "BHTA03M1D",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          title: "User Manual",
          docLink: `${S3Cdn.files}/sleeve_manual.pdf`,
        },
        {
          title: "Safety & Warranty",
          docLink: `${S3Cdn.files}/sleeve_safety_warranty.pdf`,
        },
      ],
    },
    {
      imgLink: "/images/support/downloads/download-glove-dk3.png",
      title: "TactGlove DK3",
      desc: "BHTG08M1D",
      version: "1.0",
      date: "2024-09-20",
      // DK3 매뉴얼은 EN만 (일본어 DK3 미제공) → isJa 분기 없이 단일 PDF.
      docs: [
        {
          docLink: `${S3Cdn.files}/tactglove-dk3-manual-en.pdf`,
        },
      ],
    },

    {
      imgLink: "/images/support/downloads/download-x40.jpg",
      title: "TactSuit X40",
      desc: "BHTV40D001",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: `${S3Cdn.files}/tactsuit-x-manual-en.pdf`,
        },
      ],
    },
    {
      imgLink: "/images/support/downloads/download-x16.jpg",
      title: "TactSuit X16",
      desc: "BHTV16D001",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: `${S3Cdn.files}/tactsuit-x-manual-en.pdf`,
        },
      ],
    },
    {
      imgLink: "/images/support/downloads/download-visor.jpg",
      title: "TactVisor",
      desc: "BHTH04D100",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: TACTVISOR_MANUAL(isJa),
        },
      ],
    },
    {
      imgLink: "/images/support/downloads/download-arms.jpg",
      title: "Tactosy for Arms",
      desc: "BHTTS0100",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: `${S3Cdn.files}/tactosy-for-arms-manual-en.pdf`,
        },
      ],
    },
    {
      imgLink: "/images/support/downloads/download-feet.jpg",
      title: "Tactosy for Feet",
      desc: "BHTTS0300",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: `${S3Cdn.files}/tactosy-for-feet-manual-en.pdf`,
        },
      ],
    },
  ];
};

const getSafetyList = (locale: string): ManualItem[] => {
  const isJa = locale === "ja";
  return [
    {
      imgLink: "/images/support/downloads/download-pro.jpg",
      title: "TactSuit Pro",
      desc: "BHTV32M1D",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: isJa
            ? `${S3Cdn.files}/ja/tactsuit-pro-air-safety-warranty.pdf`
            : PRO_AIR_MANUAL,
        },
      ],
    },
    {
      imgLink: "/images/tactsuit/tactsuit-air/tactsuit-air-pair.png",
      title: "TactSuit Air",
      desc: "BHTV16M1D",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: isJa
            ? `${S3Cdn.files}/ja/tactsuit-pro-air-safety-warranty.pdf`
            : PRO_AIR_MANUAL,
        },
      ],
    },
    {
      imgLink: "/images/support/downloads/download-sleeve.jpg",
      title: "TactSleeve",
      desc: "BHTA03M1D",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: `${S3Cdn.files}/sleeve_safety_warranty.pdf`,
        },
      ],
    },
    {
      imgLink: "/images/support/downloads/download-glove-dk3.png",
      title: "TactGlove DK3",
      desc: "BHTG08M1D",
      version: "1.0",
      date: "2024-09-20",
      // DK3 매뉴얼은 EN만 (일본어 DK3 미제공) → isJa 분기 없이 단일 PDF.
      docs: [
        {
          docLink: `${S3Cdn.files}/tactglove-dk3-manual-en.pdf`,
        },
      ],
    },

    {
      imgLink: "/images/support/downloads/download-x40.jpg",
      title: "TactSuit X40",
      desc: "BHTV40D001",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: `${S3Cdn.files}/tactsuit-x-manual-en.pdf`,
        },
      ],
    },
    {
      imgLink: "/images/support/downloads/download-x16.jpg",
      title: "TactSuit X16",
      desc: "BHTV16D001",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: `${S3Cdn.files}/tactsuit-x-manual-en.pdf`,
        },
      ],
    },
    {
      imgLink: "/images/support/downloads/download-visor.jpg",
      title: "TactVisor",
      desc: "BHTH04D100",
      version: "1.0",
      date: "2024-09-20",
      docs: [{ docLink: TACTVISOR_MANUAL(isJa) }],
    },
    {
      imgLink: "/images/support/downloads/download-arms.jpg",
      title: "Tactosy for Arms",
      desc: "BHTTS0100",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: `${S3Cdn.files}/tactosy-for-arms-manual-en.pdf`,
        },
      ],
    },
    {
      imgLink: "/images/support/downloads/download-feet.jpg",
      title: "Tactosy for Feet",
      desc: "BHTTS0300",
      version: "1.0",
      date: "2024-09-20",
      docs: [
        {
          docLink: `${S3Cdn.files}/tactosy-for-feet-manual-en.pdf`,
        },
      ],
    },
  ];
};

export default function ManualSection() {
  const { i18n } = useTrans(["support"]);
  const manualList = getManualList(i18n.language);

  return (
    <div className="grid w-full grid-flow-row auto-rows-fr">
      {manualList.map((item, index) => (
        <ManualCard key={index} item={item} />
      ))}
    </div>
  );
}
export function SafetyWarrantySection() {
  const { i18n } = useTrans(["support"]);
  const safetyList = getSafetyList(i18n.language);

  return (
    <div className="grid w-full grid-flow-row auto-rows-fr">
      {safetyList.map((item, index) => (
        <ManualCard key={index} item={item} />
      ))}
    </div>
  );
}

function ManualCard({ item }: { item: ManualItem }) {
  return (
    <section>
      <CardMobile item={item} />
      <CardDesktop item={item} />
    </section>
  );
}

const CardMobile = ({ item }: { item: ManualItem }) => {
  const { t } = useTrans(["support"]);
  const { imgLink, title, docs, desc } = item;
  const isSingleDocs = docs.length === 1;

  if (!isSingleDocs) {
    return (
      <div className="block sm:hidden">
        <div className="flex flex-col gap-[10px] py-3.5">
          <div className="flex grow items-center gap-x-5">
            <div className="flex flex-col items-center gap-1">
              <div className="relative h-[156px] w-[156px]">
                <NextImage src={imgLink} className={"object-cover"} />
              </div>
              <div className="text-base font-600">{t(title)}</div>
              <div className="text-gray-70 flex items-center gap-4 text-sm font-400">
                <span>{t(desc)}</span>
              </div>
            </div>

            <div className="flex flex-col justify-between gap-y-3">
              {docs.map((doc) => (
                <div key={doc.docLink} className="flex flex-col gap-2">
                  {!isSingleDocs && (
                    <h3 className="text-sm font-600">{doc.title}</h3>
                  )}
                  <a href={doc.docLink} target="_blank" className="block">
                    <DownloadButton />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-gray_12 h-px w-full" />
      </div>
    );
  }

  return (
    <div className="block sm:hidden">
      <div className="flex flex-col gap-[10px] py-3.5">
        <div className="flex grow items-center gap-x-5">
          <div className="relative h-[156px] w-[156px]">
            <NextImage src={imgLink} className={"object-cover"} />
          </div>

          <div className="flex flex-col justify-between gap-y-1">
            <div className="text-base font-600">{t(title)}</div>
            <div className="text-gray-70 flex items-center gap-4 text-sm font-400">
              <span>{t(desc)}</span>
            </div>

            {docs.map((doc) => (
              <div key={doc.docLink} className="flex flex-col gap-1 pt-3">
                {doc.title && <h3 className="text-sm font-600">{doc.title}</h3>}
                <a href={doc.docLink} target="_blank" className="block">
                  <DownloadButton />
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-gray_12 h-px w-full" />
    </div>
  );
};

const CardDesktop = ({ item }: { item: ManualItem }) => {
  const { t } = useTrans(["support"]);
  const { imgLink, title, docs, desc } = item;

  return (
    <div className="hidden sm:block">
      <div className="flex gap-[10px] py-3.5 sm:flex-row sm:items-center sm:py-5">
        <div className="flex grow items-center gap-x-5">
          <div className="relative sm:h-28 sm:w-28">
            <NextImage src={imgLink} className={"object-cover"} />
          </div>

          <div className="flex flex-col justify-between gap-y-1 sm:justify-center sm:gap-3">
            <div className="text-base font-600 sm:text-2xl sm:font-400">
              {t(title)}
            </div>
            <div className="text-gray-70 flex items-center gap-4 text-sm font-400 sm:flex-row">
              <span>{t(desc)}</span>
            </div>
          </div>
        </div>
        {docs.map((doc) => (
          <div key={doc.docLink} className="flex flex-col gap-2">
            {doc.title && <h3 className="text-sm font-600">{doc.title}</h3>}
            <a href={doc.docLink} target="_blank" className="hidden sm:block">
              <DownloadButton />
            </a>
          </div>
        ))}
      </div>
      <div className="bg-gray_12 h-px w-full" />
    </div>
  );
};

const DownloadButton = () => {
  const { t } = useTrans(["support"]);
  return (
    <Button
      variant="darkgray-text"
      size="sm"
      className="h-[42px] w-[128px] border border-coolgray-90"
    >
      {t("Download")} <DownloadIcon className="y-5 mb-1 w-5" />
    </Button>
  );
};
